import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import ScannerCard from '../create-sales-entry/ScannerCard';

const EVENT_TYPES = ['Exhibition', 'Door-to-Door', 'Other'];
const SCANNER_CONTAINER_ID = 'sample-scanner-view';

const colorsOf = (p) => Array.from(new Set((p?.skus || []).map(s => s.color).filter(Boolean)));
const sizesOf = (p) => Array.from(new Set((p?.skus || []).map(s => s.size).filter(Boolean)));

const matchProducts = (products, q) => {
  const term = q.trim().toLowerCase();
  if (!term) return [];
  return products.filter(p =>
    (p.design_code || '').toLowerCase().includes(term) ||
    (p.skus || []).some(s => (s.sku_code || '').toLowerCase().includes(term) || (s.barcode || '').toLowerCase() === term)
  ).slice(0, 25);
};

const AllocateTab = ({ events, products, reloadEvents }) => {
  const activeEvents = useMemo(() => events.filter(e => !e.is_archived), [events]);
  const [eventId, setEventId] = useState('');
  const [samples, setSamples] = useState([]);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [mode, setMode] = useState('scan'); // scan | paste | browse

  // new event form
  const [showNew, setShowNew] = useState(false);
  const [newEvt, setNewEvt] = useState({ name: '', event_type: 'Exhibition', location: '' });

  const selectedEvent = useMemo(() => events.find(e => e.id === eventId), [events, eventId]);

  const loadSamples = useCallback(async (id) => {
    if (!id) { setSamples([]); return; }
    setLoadingSamples(true);
    try {
      const res = await api.get(`/events/${id}/samples`);
      setSamples(res.data || []);
    } catch { toast.error('Failed to load samples'); }
    finally { setLoadingSamples(false); }
  }, []);

  useEffect(() => { loadSamples(eventId); }, [eventId, loadSamples]);

  const sentProductIds = useMemo(() => new Set(samples.map(s => s.product_id)), [samples]);

  const postSamples = async (items) => {
    if (!eventId) { toast.error('Select an event first'); return; }
    if (!items.length) { toast.error('Nothing to add'); return; }
    try {
      const res = await api.post(`/events/${eventId}/samples`, { samples: items });
      const { added = 0, updated = 0, not_found = [] } = res.data || {};
      toast.success(`${added} added${updated ? `, ${updated} updated` : ''}${not_found.length ? `, ${not_found.length} not found` : ''}`);
      if (not_found.length) toast.error(`Not found: ${not_found.slice(0, 8).join(', ')}${not_found.length > 8 ? '…' : ''}`);
      await loadSamples(eventId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add samples'); }
  };

  const removeSample = async (productId) => {
    try {
      await api.delete(`/events/${eventId}/samples/${productId}`);
      setSamples(prev => prev.filter(s => s.product_id !== productId));
      toast.success('Removed');
    } catch { toast.error('Failed to remove'); }
  };

  const createEvent = async () => {
    if (!newEvt.name.trim()) { toast.error('Event name required'); return; }
    try {
      const res = await api.post('/events', newEvt);
      toast.success('Event created');
      setShowNew(false);
      setNewEvt({ name: '', event_type: 'Exhibition', location: '' });
      await reloadEvents();
      setEventId(res.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create event'); }
  };

  const saveEventMeta = async (field, value) => {
    if (!eventId) return;
    try {
      await api.put(`/events/${eventId}`, { [field]: value });
      await reloadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update event'); }
  };

  const [copyFrom, setCopyFrom] = useState('');
  const [copying, setCopying] = useState(false);
  const copyFromEvent = async () => {
    if (!copyFrom) { toast.error('Select an event to copy from'); return; }
    setCopying(true);
    try {
      const res = await api.post(`/events/${eventId}/samples/copy-from/${copyFrom}`);
      const { added = 0, skipped = 0 } = res.data || {};
      toast.success(`Copied ${added} samples${skipped ? `, ${skipped} already present` : ''}`);
      setCopyFrom('');
      await loadSamples(eventId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to copy samples'); }
    finally { setCopying(false); }
  };

  const exportSamples = async (format) => {
    try {
      const res = await api.get(`/events/${eventId}/samples/export`, { params: { format }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const safe = (selectedEvent?.name || 'event').replace(/\s+/g, '_');
      a.download = `${safe}_samples.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const downloadBarcodeExcel = async () => {
    try {
      const res = await api.get(`/events/${eventId}/samples/barcode-excel`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      const safe = (selectedEvent?.name || 'event').replace(/\s+/g, '_');
      a.download = `barcodes-${safe}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Barcode Excel downloaded!');
    } catch { toast.error('Failed to download barcode Excel'); }
  };

  return (
    <div className="space-y-4">
      {/* Event selector */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs font-semibold text-gray-600">Event</label>
              <select value={eventId} onChange={e => setEventId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="allocate-event-select">
                <option value="">Select an event…</option>
                {activeEvents.map(e => (
                  <option key={e.id} value={e.id}>{e.name}{e.event_type ? ` · ${e.event_type}` : ''}{e.location ? ` · ${e.location}` : ''}</option>
                ))}
              </select>
            </div>
            <Button variant="outline" onClick={() => setShowNew(v => !v)} data-testid="allocate-new-event-btn">+ New Event</Button>
          </div>

          {showNew && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 p-3 rounded-lg bg-gray-50 border" data-testid="new-event-form">
              <Input placeholder="Event name" value={newEvt.name} onChange={e => setNewEvt({ ...newEvt, name: e.target.value })} className="h-9 text-sm" data-testid="new-event-name" />
              <select value={newEvt.event_type} onChange={e => setNewEvt({ ...newEvt, event_type: e.target.value })} className="flex h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="new-event-type">
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <Input placeholder="Location (e.g. Haryana)" value={newEvt.location} onChange={e => setNewEvt({ ...newEvt, location: e.target.value })} className="h-9 text-sm" data-testid="new-event-location" />
              <Button onClick={createEvent} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-event-save">Create</Button>
            </div>
          )}

          {selectedEvent && (
            <div className="flex items-center gap-3 flex-wrap text-sm" data-testid="event-meta-editor">
              <span className="text-gray-500">Type:</span>
              <select value={selectedEvent.event_type || ''} onChange={e => saveEventMeta('event_type', e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-sm" data-testid="event-type-edit">
                <option value="">—</option>
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <span className="text-gray-500">Location:</span>
              <Input defaultValue={selectedEvent.location || ''} onBlur={e => saveEventMeta('location', e.target.value)} placeholder="—" className="h-8 w-40 text-sm" data-testid="event-location-edit" />
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">{samples.length} samples</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {eventId && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex gap-1">
              {['scan', 'paste', 'browse'].map(m => (
                <button key={m} onClick={() => setMode(m)}
                  className={`px-3 py-1.5 text-sm rounded-md capitalize ${mode === m ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  data-testid={`add-mode-${m}`}>
                  {m === 'scan' ? 'Scan / Search' : m === 'paste' ? 'Paste List' : 'Browse'}
                </button>
              ))}
            </div>
            {mode === 'scan' && <ScanSearch products={products} sentProductIds={sentProductIds} onAdd={postSamples} />}
            {mode === 'paste' && <PasteList onAdd={postSamples} />}
            {mode === 'browse' && <BrowseList products={products} sentProductIds={sentProductIds} onAdd={postSamples} />}
          </CardContent>
        </Card>
      )}

      {eventId && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <h3 className="text-sm font-bold text-gray-800">Samples carried to this event ({samples.length})</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={copyFrom} onChange={e => setCopyFrom(e.target.value)}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="copy-from-select">
                  <option value="">Copy samples from…</option>
                  {events.filter(e => e.id !== eventId).map(e => (
                    <option key={e.id} value={e.id}>{e.name}{e.is_archived ? ' (archived)' : ''}</option>
                  ))}
                </select>
                <Button variant="outline" size="sm" disabled={!copyFrom || copying} onClick={copyFromEvent} data-testid="copy-from-btn">
                  {copying ? 'Copying…' : 'Copy'}
                </Button>
                <div className="h-5 w-px bg-gray-200" />
                <Button variant="outline" size="sm" disabled={samples.length === 0} onClick={() => exportSamples('xlsx')} data-testid="samples-export-xlsx">Excel</Button>
                <Button variant="outline" size="sm" disabled={samples.length === 0} onClick={() => exportSamples('pdf')} data-testid="samples-export-pdf">PDF</Button>
                <Button variant="outline" size="sm" disabled={samples.length === 0} className="border-green-300 text-green-700 hover:bg-green-50" onClick={downloadBarcodeExcel} data-testid="samples-barcode-excel">Barcode Excel</Button>
              </div>
            </div>
            {loadingSamples ? <p className="text-sm text-gray-400">Loading…</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="samples-table">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b">
                      <th className="px-3 py-2 font-semibold">Design</th>
                      <th className="px-3 py-2 font-semibold">Category</th>
                      <th className="px-3 py-2 font-semibold">Colours</th>
                      <th className="px-3 py-2 font-semibold">Sizes</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {samples.map(s => (
                      <tr key={s.id} className="border-b border-gray-100" data-testid={`sample-row-${s.product_id}`}>
                        <td className="px-3 py-2 font-medium text-gray-800">{s.design_code}</td>
                        <td className="px-3 py-2 text-gray-600">{s.category || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {(!s.colors || s.colors.length === 0) ? <span className="text-emerald-700">All ({s.all_colors_count})</span> : s.colors.join(', ')}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{(!s.sizes || s.sizes.length === 0) ? 'All' : s.sizes.join(', ')}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => removeSample(s.product_id)} className="text-red-500 hover:text-red-700 text-xs" data-testid={`sample-remove-${s.product_id}`}>Remove</button>
                        </td>
                      </tr>
                    ))}
                    {samples.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No samples added yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// --- Scan / Search ---
const ScanSearch = ({ products, sentProductIds, onAdd }) => {
  const [q, setQ] = useState('');
  const [picker, setPicker] = useState(null); // product being colour-picked
  const [scannerActive, setScannerActive] = useState(false);
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const matches = useMemo(() => matchProducts(products, q), [products, q]);

  const addAll = (p) => { onAdd([{ product_id: p.id, design_code: p.design_code, colors: [], sizes: [] }]); setQ(''); };

  const findByCode = useCallback((text) => {
    const term = (text || '').trim().toLowerCase();
    if (!term) return null;
    return products.find(p => (p.design_code || '').toLowerCase() === term
      || (p.skus || []).some(s => (s.sku_code || '').toLowerCase() === term || (s.barcode || '').toLowerCase() === term)) || null;
  }, [products]);

  const handleScannedCode = useCallback((text) => {
    const p = findByCode(text);
    if (p) { onAdd([{ product_id: p.id, design_code: p.design_code, colors: [], sizes: [] }]); }
    else { toast.error(`No design matches "${text}"`); }
  }, [findByCode, onAdd]);

  const startScanner = useCallback(async () => {
    setScannerActive(true);
    const { Html5Qrcode } = await import('html5-qrcode');
    setTimeout(() => {
      const el = document.getElementById(SCANNER_CONTAINER_ID);
      if (!el) { setScannerActive(false); return; }
      const scanner = new Html5Qrcode(SCANNER_CONTAINER_ID);
      scannerRef.current = scanner;
      scanner.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 250, height: 150 }, aspectRatio: 2.0 },
        (t) => handleScannedCode(t), () => {}).catch(() => { toast.error('Camera not available. Use Camera Photo or a wireless scanner.'); setScannerActive(false); });
    }, 200);
  }, [handleScannedCode]);

  const stopScanner = useCallback(() => {
    if (scannerRef.current) { try { scannerRef.current.stop().then(() => { try { scannerRef.current.clear(); } catch (e) {} }).catch(() => {}); } catch (e) {} scannerRef.current = null; }
    setScannerActive(false);
  }, []);

  const handleFileScan = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('se-file-scanner-tmp');
      const result = await scanner.scanFileV2(file, true);
      handleScannedCode(result.decodedText);
      scanner.clear();
    } catch { toast.error('Could not read barcode from photo. Try a clearer image.'); }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleScannedCode]);

  useEffect(() => () => { if (scannerRef.current) { try { scannerRef.current.stop().catch(() => {}); } catch (e) {} scannerRef.current = null; } }, []);

  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const term = q.trim().toLowerCase();
    const exact = products.find(p => (p.design_code || '').toLowerCase() === term
      || (p.skus || []).some(s => (s.sku_code || '').toLowerCase() === term || (s.barcode || '').toLowerCase() === term));
    if (exact) addAll(exact);
    else if (matches.length === 1) addAll(matches[0]);
    else if (matches.length === 0) toast.error('No matching design');
  };

  return (
    <div className="space-y-2">
      <ScannerCard
        scannerActive={scannerActive}
        startScanner={startScanner}
        stopScanner={stopScanner}
        fileInputRef={fileInputRef}
        handleFileScan={handleFileScan}
        scannerContainerId={SCANNER_CONTAINER_ID}
      />
      <Input autoFocus value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKeyDown}
        placeholder="Scan barcode or type design code / SKU, then Enter…" className="h-9 text-sm" data-testid="scan-input" />
      {q.trim() && (
        <div className="border rounded-lg max-h-72 overflow-y-auto divide-y" data-testid="scan-results">
          {matches.map(p => {
            const already = sentProductIds.has(p.id);
            return (
              <div key={p.id} className="flex items-center gap-3 px-3 py-2">
                <span className="font-medium text-sm text-gray-800 flex-1">{p.design_code}
                  <span className="text-xs text-gray-400 ml-2">{colorsOf(p).length} colours · {p.category || ''}</span>
                  {already && <span className="ml-2 text-[10px] text-emerald-600">added</span>}
                </span>
                <button onClick={() => setPicker(p)} className="text-xs px-2 py-1 rounded border text-gray-600 hover:bg-gray-50" data-testid={`scan-choose-${p.design_code}`}>Choose colours</button>
                <button onClick={() => addAll(p)} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`scan-addall-${p.design_code}`}>Add all colours</button>
              </div>
            );
          })}
          {matches.length === 0 && <p className="px-3 py-4 text-sm text-gray-400">No match</p>}
        </div>
      )}
      {picker && (
        <ColorPicker product={picker} onClose={() => setPicker(null)}
          onConfirm={(colors, sizes) => { onAdd([{ product_id: picker.id, design_code: picker.design_code, colors, sizes }]); setPicker(null); setQ(''); }} />
      )}
    </div>
  );
};

const ColorPicker = ({ product, onClose, onConfirm }) => {
  const allColors = colorsOf(product);
  const allSizes = sizesOf(product);
  const [colors, setColors] = useState(allColors);
  const [sizes, setSizes] = useState([]);
  const toggle = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  return (
    <div className="border rounded-lg p-3 bg-amber-50/40 space-y-2" data-testid="color-picker">
      <p className="text-sm font-semibold text-gray-800">{product.design_code} — choose colours / sizes</p>
      <div>
        <p className="text-xs text-gray-500 mb-1">Colours (none ticked = all)</p>
        <div className="flex flex-wrap gap-1.5">
          {allColors.map(c => (
            <button key={c} onClick={() => toggle(colors, setColors, c)}
              className={`text-xs px-2 py-1 rounded-full border ${colors.includes(c) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600'}`}>{c}</button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs text-gray-500 mb-1">Sizes (none ticked = all)</p>
        <div className="flex flex-wrap gap-1.5">
          {allSizes.map(z => (
            <button key={z} onClick={() => toggle(sizes, setSizes, z)}
              className={`text-xs px-2 py-1 rounded-full border ${sizes.includes(z) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600'}`}>{z}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onConfirm(colors.length === allColors.length ? [] : colors, sizes.length === allSizes.length ? [] : sizes)} data-testid="color-picker-add">Add</Button>
      </div>
    </div>
  );
};

// --- Paste list ---
const PasteList = ({ onAdd }) => {
  const [text, setText] = useState('');
  const submit = () => {
    const codes = text.split(/[\n,;\t ]+/).map(s => s.trim()).filter(Boolean);
    if (!codes.length) { toast.error('Paste some design codes'); return; }
    onAdd(codes.map(c => ({ design_code: c, colors: [], sizes: [] })));
    setText('');
  };
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">Paste design codes (separated by new lines, commas or spaces). All colours/sizes are added for each.</p>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={5}
        className="w-full rounded-md border border-input bg-background p-2 text-sm" placeholder={'1004\n1014\n1021'} data-testid="paste-input" />
      <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="paste-add-btn">Add designs</Button>
    </div>
  );
};

// --- Browse ---
const BrowseList = ({ products, sentProductIds, onAdd }) => {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(new Set());
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = term ? products.filter(p => (p.design_code || '').toLowerCase().includes(term) || (p.category || '').toLowerCase().includes(term)) : products;
    return base.slice(0, 2000);
  }, [products, q]);
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const listIds = useMemo(() => list.map(p => p.id), [list]);
  const allSelected = listIds.length > 0 && listIds.every(id => selected.has(id));
  const someSelected = listIds.some(id => selected.has(id));
  const selectAllRef = useRef(null);
  useEffect(() => { if (selectAllRef.current) selectAllRef.current.indeterminate = !allSelected && someSelected; }, [allSelected, someSelected]);
  const toggleSelectAll = () => setSelected(prev => {
    const n = new Set(prev);
    if (allSelected) listIds.forEach(id => n.delete(id));
    else listIds.forEach(id => n.add(id));
    return n;
  });
  const addSelected = () => {
    const items = products.filter(p => selected.has(p.id)).map(p => ({ product_id: p.id, design_code: p.design_code, colors: [], sizes: [] }));
    if (!items.length) { toast.error('Select at least one design'); return; }
    onAdd(items);
    setSelected(new Set());
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter designs…" className="h-9 text-sm" data-testid="browse-search" />
        <Button onClick={addSelected} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="browse-add-btn">Add {selected.size || ''} selected</Button>
      </div>
      <div className="border rounded-lg max-h-80 overflow-y-auto divide-y" data-testid="browse-list">
        {list.length > 0 && (
          <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 sticky top-0 z-10">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" ref={selectAllRef} checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4" data-testid="browse-select-all" />
              <span className="text-xs font-semibold text-gray-600">Select all ({list.length})</span>
            </label>
            {selected.size > 0 && (
              <button onClick={() => setSelected(new Set())} className="text-xs text-red-500 hover:underline" data-testid="browse-clear-all">Clear ({selected.size})</button>
            )}
          </div>
        )}
        {list.map(p => (
          <label key={p.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50">
            <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} className="w-4 h-4" data-testid={`browse-check-${p.design_code}`} />
            <span className="font-medium text-sm text-gray-800 flex-1">{p.design_code}
              <span className="text-xs text-gray-400 ml-2">{colorsOf(p).length} colours · {p.category || ''}</span>
            </span>
            {sentProductIds.has(p.id) && <span className="text-[10px] text-emerald-600">added</span>}
          </label>
        ))}
        {list.length === 0 && <p className="px-3 py-4 text-sm text-gray-400">No designs</p>}
      </div>
    </div>
  );
};

export default AllocateTab;
